import React, {
	useContext,
	useCallback,
	useMemo,
	useState,
	createContext,
	type ChangeEvent,
} from 'react';

import { type ViralSettingsState } from './types';
import { useViralSettings } from './useViralSettings';

import { useProducts } from '../ProductSelect';
import { useInvitePeopleContext } from '../InvitePeople';
import { useInviteeListStateContext } from '../InviteeList';

const ViralSettingsStateContext = createContext<ViralSettingsState | undefined>(undefined);

interface ViralSettingsContextProps {
	children: React.ReactElement;
	disableViralSettings: boolean;
	onInviteeListFocus?: () => void;
}

export const ViralSettingsContext = ({
	children,
	disableViralSettings,
	onInviteeListFocus: onInviteeListFocusProp,
}: ViralSettingsContextProps) => {
	const [uniqueEmailDomains, setUniqueEmailDomains] = useState<string[]>([]);
	const [selectHasBeenFocused, setSelectHasBeenFocused] = useState(false);
	const onInviteeListFocus = useCallback(() => {
		setSelectHasBeenFocused(true);
		onInviteeListFocusProp?.();
	}, [setSelectHasBeenFocused, onInviteeListFocusProp]);

	const { emails } = useInviteeListStateContext();
	const { cloudId, userRole } = useInvitePeopleContext();
	const { selectedProducts } = useProducts();
	const {
		viralSettingsByDomain,
		eligibleDomains,
		viralSettingsDESEligibleStates,
		showOpenInvite,
		handleViralSettingsByDomainCheckbox,
		enableOpenInvite,
		setEnableOpenInvite,
		isViralSettingsEnabled,
	} = useViralSettings({
		domains: uniqueEmailDomains,
		cloudId: cloudId || '',
		products: selectedProducts,
		role: userRole,
		selectHasBeenFocused,
		emails,
		disableViralSettings,
	});

	const onOpenInviteChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setEnableOpenInvite(event?.target.checked);
		},
		[setEnableOpenInvite],
	);

	const contextValue: ViralSettingsState = useMemo(() => {
		return {
			viralSettingsByDomain,
			eligibleDomains,
			viralSettingsDESEligibleStates,
			showOpenInvite,
			handleViralSettingsByDomainCheckbox,
			openInviteEnabled: enableOpenInvite,
			onOpenInviteChange,
			setEnableOpenInvite,
			isViralSettingsEnabled,
			uniqueEmailDomains,
			setUniqueEmailDomains,
			selectHasBeenFocused,
			onInviteeListFocus,
		};
	}, [
		viralSettingsByDomain,
		eligibleDomains,
		viralSettingsDESEligibleStates,
		showOpenInvite,
		handleViralSettingsByDomainCheckbox,
		enableOpenInvite,
		onOpenInviteChange,
		setEnableOpenInvite,
		isViralSettingsEnabled,
		uniqueEmailDomains,
		setUniqueEmailDomains,
		selectHasBeenFocused,
		onInviteeListFocus,
	]);

	return (
		<ViralSettingsStateContext.Provider value={contextValue}>
			{children}
		</ViralSettingsStateContext.Provider>
	);
};

export const useViralSettingsContext = () => {
	const state = useContext(ViralSettingsStateContext);

	if (state === undefined) {
		throw new Error('useViralSettingsContext must be used within a ViralSettingsContext provider');
	}
	return state;
};
